<template>
  <section class="py-12 lg:py-16">
    <div class="mx-auto max-w-6xl px-1 md:px-4">
      <div
        v-if="title && titleAbove"
        class="innerContent mb-8 md:mb-10 text-center"
        :class="titleAboveClass"
        v-html="title"
      />
      <div
        class="grid gap-8 lg:grid-cols-2"
        :class="[
          reversed ? 'lg:flex-row-reverse' : '',
          contentTop ? 'items-start' : 'items-center',
        ]"
      >
        <div
          v-if="img"
          class="order-1 shrink-0 lg:flex"
          :class="[
            reversed ? 'lg:order-2' : 'lg:order-1',
            imageCentered
              ? 'lg:justify-center lg:items-center'
              : 'lg:justify-end',
          ]"
        >
          <img
            v-if="img1x"
            :src="img"
            :alt="alt"
            :width="width"
            :height="height"
            class="w-full max-w-[457px] rounded-lg object-cover lg:ml-auto"
            loading="lazy"
          />
          <NuxtImg
            v-else
            :src="img"
            :alt="alt"
            :width="width"
            :height="height"
            format="webp"
            class="w-full rounded-lg object-cover"
          />
        </div>
        <div class="order-1" :class="reversed ? 'lg:order-1' : 'lg:order-2'">
          <div
            v-if="title && !titleAbove"
            class="innerContent mb-4 text-xl font-semibold"
            v-html="title"
          />
          <div
            class="innerContent prose max-w-none text-gray-700 prose-p:mb-3 prose-ul:my-3 prose-li:my-0.5"
            :class="{ 'list-check': listStyle === 'check' }"
            v-html="textContent"
          />
          <div
            v-if="buttonText && (buttonTo || buttonLinkTwo) && !buttonsBelow"
            class="mt-6 flex flex-wrap items-center justify-start gap-3"
          >
            <Button
              v-if="buttonText && buttonTo && buttonVariant"
              :as="'a'"
              :href="linkHref(buttonTo)"
              :variant="buttonVariant"
              :size="buttonSize"
            >
              {{ buttonText }}
            </Button>
            <Button
              v-else-if="buttonText && buttonTo"
              :as="'a'"
              :href="linkHref(buttonTo)"
              :size="buttonSize"
              class="rounded-lg bg-landing-primary px-6 py-3 font-medium text-white transition-colors hover:bg-landing-primary-hover"
            >
              {{ buttonText }}
            </Button>
            <Button
              v-if="buttonTextTwo && buttonLinkTwo"
              :as="'a'"
              :href="
                isExternalLink(buttonLinkTwo)
                  ? buttonLinkTwo
                  : linkHref(buttonLinkTwo)
              "
              :target="buttonTwoTarget"
              :variant="buttonVariantTwo"
              :size="buttonSize"
            >
              {{ buttonTextTwo }}
            </Button>
          </div>
        </div>
      </div>
      <div
        v-if="buttonText && (buttonTo || buttonLinkTwo) && buttonsBelow"
        class="mt-10 flex flex-wrap flex-col md:flex-row items-start md:items-center justify-start gap-3"
      >
        <Button
          v-if="buttonText && buttonTo && buttonVariant"
          :as="'a'"
          :href="linkHref(buttonTo)"
          :variant="buttonVariant"
          :size="buttonSize"
        >
          {{ buttonText }}
        </Button>
        <Button
          v-else-if="buttonText && buttonTo"
          :as="'a'"
          :href="linkHref(buttonTo)"
          :size="buttonSize"
          class="rounded-lg bg-landing-primary px-6 py-3 font-medium text-white transition-colors hover:bg-landing-primary-hover"
        >
          {{ buttonText }}
        </Button>
        <Button
          v-if="buttonTextTwo && buttonLinkTwo"
          :as="'a'"
          :href="
            isExternalLink(buttonLinkTwo)
              ? buttonLinkTwo
              : linkHref(buttonLinkTwo)
          "
          :target="buttonTwoTarget"
          :variant="buttonVariantTwo"
          :size="buttonSize"
        >
          {{ buttonTextTwo }}
        </Button>
      </div>
    </div>
  </section>
</template>
<script setup lang="ts">
import { Button, type ButtonVariants } from "~/shared/components/ui/button";

withDefaults(
  defineProps<{
    title?: string;
    textContent: string;
    img?: string;
    alt?: string;
    width?: number;
    height?: number;
    buttonText?: string;
    buttonTo?: string;
    buttonVariant?: ButtonVariants["variant"];
    /** Second button (e.g. "How it works") – bridge style */
    buttonTextTwo?: string;
    buttonLinkTwo?: string;
    buttonVariantTwo?: ButtonVariants["variant"];
    buttonTwoTarget?: "_self" | "_blank";
    reversed?: boolean;
    /** Center image in its grid cell (e.g. right column when reversed) */
    imageCentered?: boolean;
    /** Use 1x image only (no srcset), with top-right alignment on desktop */
    img1x?: boolean;
    /** List style: 'disc' (default) or 'check' (✓) */
    listStyle?: "disc" | "check";
    /** Button size (default lg) */
    buttonSize?: "lg" | "xl";
    /** Align content column to top (for hero blocks) */
    contentTop?: boolean;
    /** Render title centered above the text+image block */
    titleAbove?: boolean;
    /** Put buttons in a row below the image+text block, left-aligned */
    buttonsBelow?: boolean;
    /** Class for title when titleAbove (e.g. landing_h2, landing_2) */
    titleAboveClass?: string;
  }>(),
  {
    alt: "",
    width: 350,
    height: 250,
    reversed: false,
    img1x: false,
    imageCentered: false,
    buttonVariantTwo: "landingOutline",
    buttonTwoTarget: "_self",
    buttonSize: "lg",
    contentTop: false,
    titleAbove: false,
    buttonsBelow: false,
    titleAboveClass: "text-center text-xl font-semibold md:mb-10",
  },
);
const { linkHref } = useNativeRouteLink();
function isExternalLink(href: string) {
  return href.startsWith("http://") || href.startsWith("https://");
}
</script>

<style scoped>
.innerContent :deep(a) {
  @apply text-teal-600 underline;
}
.innerContent :deep(ul) {
  @apply list-disc pl-5;
}
.innerContent.list-check :deep(ul) {
  list-style: none;
  padding-left: 0;
}
.innerContent.list-check :deep(ul li) {
  position: relative;
  padding-left: 1.5rem;
}
.innerContent.list-check :deep(ul li::before) {
  content: "✓";
  position: absolute;
  left: 0;
  font-weight: 700;
  color: #000;
}
.innerContent :deep(strong) {
  @apply font-semibold;
}
</style>
